import { ASSETS_STORE, openPlayerDatabase, } from './idb/database.js';
import { idbRequest, idbTransactionDone } from './idb/request.js';
function toBlobUri(bytes, mimeType) {
    return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}
/**
 * Durable AssetStore backed by IndexedDB.
 * Survives reloads; recreates blob URLs on each process lifetime.
 */
export class IndexedDbAssetStore {
    openDb;
    dbPromise = null;
    /** Session blob URLs — revoked on replace / delete / clear. */
    objectUrls = new Map();
    constructor(openDb = openPlayerDatabase) {
        this.openDb = openDb;
    }
    db() {
        if (!this.dbPromise)
            this.dbPromise = this.openDb();
        return this.dbPromise;
    }
    async get(assetId) {
        const db = await this.db();
        const record = await idbRequest(db.transaction(ASSETS_STORE, 'readonly').objectStore(ASSETS_STORE).get(assetId));
        if (!record)
            return null;
        const stored = record;
        const existingUrl = this.objectUrls.get(assetId);
        if (existingUrl) {
            return {
                assetId: stored.assetId,
                sha256: stored.sha256,
                mimeType: stored.mimeType,
                localUri: existingUrl,
                bytes: stored.bytes,
            };
        }
        const localUri = toBlobUri(stored.bytes, stored.mimeType);
        this.objectUrls.set(assetId, localUri);
        return {
            assetId: stored.assetId,
            sha256: stored.sha256,
            mimeType: stored.mimeType,
            localUri,
            bytes: stored.bytes,
        };
    }
    async put(asset) {
        const db = await this.db();
        const record = {
            assetId: asset.assetId,
            sha256: asset.sha256,
            mimeType: asset.mimeType,
            bytes: asset.bytes,
        };
        const tx = db.transaction(ASSETS_STORE, 'readwrite');
        tx.objectStore(ASSETS_STORE).put(record);
        await idbTransactionDone(tx);
        this.revoke(asset.assetId);
        const localUri = asset.localUri.startsWith('blob:') || asset.localUri.startsWith('memory:')
            ? asset.localUri
            : toBlobUri(asset.bytes, asset.mimeType);
        this.objectUrls.set(asset.assetId, localUri);
    }
    async delete(assetId) {
        const db = await this.db();
        const tx = db.transaction(ASSETS_STORE, 'readwrite');
        tx.objectStore(ASSETS_STORE).delete(assetId);
        await idbTransactionDone(tx);
        this.revoke(assetId);
    }
    async clear() {
        const db = await this.db();
        const tx = db.transaction(ASSETS_STORE, 'readwrite');
        tx.objectStore(ASSETS_STORE).clear();
        await idbTransactionDone(tx);
        for (const assetId of [...this.objectUrls.keys()]) {
            this.revoke(assetId);
        }
    }
    revoke(assetId) {
        const url = this.objectUrls.get(assetId);
        if (!url)
            return;
        if (url.startsWith('blob:') && typeof URL !== 'undefined' && URL.revokeObjectURL) {
            URL.revokeObjectURL(url);
        }
        this.objectUrls.delete(assetId);
    }
}
//# sourceMappingURL=indexed-db-asset-store.js.map